import { Component, useMemo, useState, useEffect } from "react";
import { useTable, useSortBy } from "react-table";
import { requireToken, SessionExpiredError } from "../auth/authService";
import { getFromMCAPI } from "./merchantConfigurationApi";
import { configs } from "../configurations";
import { PageHeader } from "./PageHeader";
import { CopyableId } from "./CopyableId";

function InvitesTable({ data }) {
  const invites = useMemo(() => data, [data]);
  const columns = useMemo(
    () => [
      {
        header: "TowPay Merchant ID",
        accessor: "merchantId",
        Cell: ({ value }) => (
          <CopyableId value={value} label="TowPay merchant ID" />
        ),
      },
      {
        //  Not on the invite record; merged in from the merchant list by merchantId. Blank
        //  until the merchant finishes onboarding and Stripe issues an account.
        header: "Stripe Account ID",
        accessor: "paymentProcessorAccountId",
      },
      {
        header: "Partner",
        accessor: "partnerName",
      },
      {
        header: "Country",
        accessor: "country",
      },
      {
        header: "Controller Email",
        accessor: "emailAddress",
      },
      {
        header: "Sent By",
        accessor: "sentBy",
      },
      {
        header: "Sent On",
        accessor: "inviteSent",
        //  Date in the column, full timestamp on hover. The accessor is unchanged, so
        //  sorting still works off the raw value, which this column defaults to.
        Cell: ({ value }) => {
          //  Guarded: new Date(undefined).toLocaleDateString() renders the string
          //  "Invalid Date" rather than failing.
          if (!value) return null;
          const sent = new Date(value);
          return (
            <span title={sent.toLocaleString()}>
              {sent.toLocaleDateString()}
            </span>
          );
        },
      },
    ],
    [],
  );

  const tableInstance = useTable(
    {
      columns,
      data: invites,
      initialState: {
        sortBy: [
          {
            id: "inviteSent",
            desc: true,
          },
        ],
      },
    },
    useSortBy,
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    //  table-responsive gives the table its own horizontal scrollbar rather than letting
    //  it widen the page, and text-nowrap is inherited by every cell so rows stay one line
    //  tall. Both are the safety net at narrow widths, not the main fix: the columns were
    //  trimmed so this rarely has to scroll at all.
    <div className="table-responsive">
      <table
        {...getTableProps()}
        className="table table-striped text-nowrap align-middle"
        aria-labelledby="tableLabel"
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {/*  text-nowrap keeps the label and its sort arrow on one line. Without
                     it a narrow column (Country) wrapped the arrow underneath, making
                     that one header two lines tall and knocking it out of line with the
                     rest. The arrow is always rendered, just invisible when the column
                     is unsorted, so the header does not jump width when you sort it. */}
                  <div className="text-nowrap">
                    {column.render("header")}
                    <i
                      className={`bi ms-1 ${
                        column.isSorted
                          ? column.isSortedDesc
                            ? "bi-arrow-down"
                            : "bi-arrow-up"
                          : "bi-arrow-up invisible"
                      }`}
                      aria-hidden="true"
                    />
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.length === 0 ? (
            <tr>
              {/*  colSpan off the column count, rather than the four hardcoded empty cells
                   this had. Those never matched the eight columns, and adding a ninth
                   would have widened the gap further. */}
              <td colSpan={columns.length}>No Invites, currently.</td>
            </tr>
          ) : (
            rows.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function GetMerchantInviteData({ configData }) {
  const [apiData, setApiData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use useEffect to load invites data
  useEffect(() => {
    if (
      !apiData &&
      configData.merchantConfigurationBaseUri !== undefined &&
      configData.merchantInvitesEndpoint !== undefined
    ) {
      setIsLoading(true);
      setError(null);

      let accessToken;
      try {
        accessToken = requireToken();
      } catch (tokenError) {
        //  requireToken is already navigating to the login screen.
        if (!(tokenError instanceof SessionExpiredError))
          console.error(tokenError);
        setIsLoading(false);
        return;
      }

      const base = configData.merchantConfigurationBaseUri;

      Promise.all([
        getFromMCAPI(accessToken, base + configData.merchantInvitesEndpoint),
        //  The payment processor account id is not on the invite record, it lives on the
        //  merchant, and both carry merchantId. Fetched alongside rather than joined by the
        //  API so this needs no API change or deploy.
        //
        //  Caught here rather than letting Promise.all reject: the account id is one
        //  column, and losing it must not take the invites list down with it. On failure
        //  the column renders blank and the page is otherwise intact.
        getFromMCAPI(accessToken, base + "/api/merchants").catch(
          (merchantError) => {
            console.error(
              "Failed to fetch merchants for the account id column:",
              merchantError,
            );
            return [];
          },
        ),
      ])
        .then(([invites, merchants]) => {
          const accountIdByMerchantId = new Map(
            (Array.isArray(merchants) ? merchants : []).map((merchant) => [
              merchant.merchantId,
              merchant.paymentProcessorAccountId,
            ]),
          );

          setApiData(
            (Array.isArray(invites) ? invites : []).map((invite) => ({
              ...invite,
              paymentProcessorAccountId:
                accountIdByMerchantId.get(invite.merchantId) || "",
            })),
          );
          setIsLoading(false);
        })
        .catch((apiError) => {
          console.error("Failed to fetch invites data:", apiError);
          setError("Failed to load invites data. Please try again.");
          setIsLoading(false);
        });
    }
  }, [configData, apiData]);

  // Render loading spinner
  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading merchant invites...</p>
      </div>
    );
  }

  // Render error message
  if (error) {
    return <p className="text-danger">{error}</p>;
  }

  // No data available
  if (!apiData) {
    return <p>No merchant invite data available.</p>;
  }

  return <InvitesTable data={apiData} />;
}

export class ManageInvites extends Component {
  static displayName = ManageInvites.name;

  constructor(props) {
    super(props);

    this.state = {
      invites: [],
      loading: true,
      envConfigs: {},
    };
  }

  async componentDidMount() {
    const envConfigs = await configs();
    this.setState({ envConfigs });
  }

  render() {
    return (
      <div>
        <PageHeader
          title="Invite History"
          titleId="tableLabel"
          lead="Every merchant invitation that has been sent, and the merchant it created."
        />
        <GetMerchantInviteData configData={this.state.envConfigs} />
      </div>
    );
  }
}
